import React, { useEffect } from "react";
import { NavigationBlocker } from "../components/common/NavigationBlocker";
import {
  useTaxEditorStore,
  selectTaxIsDirty,
} from "../store/useTaxEditorStore";
import { useShopifySaveBar } from "../hooks/useShopifySaveBar";

// ---------- Component ----------

const TaxEditorPage: React.FC = () => {
  const settings = useTaxEditorStore((s) => s.settings);

  const isLoading = useTaxEditorStore((s) => s.isLoading);
  const isSaving = useTaxEditorStore((s) => s.isSaving);
  const error = useTaxEditorStore((s) => s.error);
  const isDirty = useTaxEditorStore(selectTaxIsDirty);

  const loadFromBackend = useTaxEditorStore((s) => s.loadFromBackend);
  const updateSetting = useTaxEditorStore((s) => s.updateSetting);
  const save = useTaxEditorStore((s) => s.save);
  const discard = useTaxEditorStore((s) => s.discardChanges);

  useEffect(() => {
    loadFromBackend();
  }, [loadFromBackend]);

  useShopifySaveBar({ isDirty, isSaving, onSave: save, onDiscard: discard });

  // --- Loading ---
  if (isLoading) {
    return (
      <div>
        <s-page heading="Impuestos">
          <s-box padding="large-400">
            <s-stack direction="block" gap="base" alignItems="center">
              <s-spinner />
              <s-text color="subdued">Cargando configuración...</s-text>
            </s-stack>
          </s-box>
        </s-page>
      </div>
    );
  }

  // --- Error ---
  if (error) {
    return (
      <div>
        <s-page heading="Impuestos">
          <s-banner tone="critical">
            <s-text>Error al cargar: {error}</s-text>
          </s-banner>
        </s-page>
      </div>
    );
  }

  return (
    <div>
      <NavigationBlocker isDirty={isDirty} />

      <s-page heading="Editor de Impuestos">
        {/* Description */}
        <s-section>
          <s-grid gridTemplateColumns="1fr 2fr" gap="large-200">
            <s-stack direction="block" gap="small-200">
              <s-text type="strong">
                Añade un cálculo de impuestos a los pedidos desde tu formulario
                de pago contra reembolso (opcional)
              </s-text>
            </s-stack>

            <s-box
              background="base"
              border="small subdued solid"
              borderRadius="large-100"
              padding="large-200"
            >
              <s-stack direction="block" gap="base">
                {/* Enable checkbox */}
                <s-checkbox
                  {...({
                    label: "Habilitar el cálculo de impuestos",
                    checked: settings.enabled,
                    onChange: (event: any) =>
                      updateSetting("enabled", event.currentTarget.checked),
                  } as any)}
                />

                {/* Apply to + Tax rate */}
                <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                  <s-select
                    label="Aplicar en:"
                    name="apply_to"
                    value={settings.apply_to}
                    onChange={(event: any) =>
                      updateSetting("apply_to", event.target.value)
                    }
                  >
                    <s-option value="products_only">Solo productos</s-option>
                    <s-option value="products_and_shipping">
                      Productos y envío
                    </s-option>
                  </s-select>

                  <s-stack direction="inline" gap="small-200" alignItems="end">
                    <s-text-field
                      label="Tasa de impuesto %"
                      name="tax_rate"
                      value={String(settings.rate)}
                      placeholder="0"
                      onInput={(event: unknown) => {
                        const val = parseFloat(
                          (event as { currentTarget: { value: string } })
                            .currentTarget.value
                        );
                        updateSetting("rate", isNaN(val) ? 0 : val);
                      }}
                    />
                  </s-stack>
                </s-grid>

                {/* Tax Name + Included in price */}
                <s-grid gridTemplateColumns="1fr 1fr" gap="base">
                  <s-text-field
                    label="Nombre del impuesto"
                    name="tax_name"
                    value={settings.name}
                    placeholder="VAT"
                    onInput={(event: unknown) =>
                      updateSetting(
                        "name",
                        (event as { currentTarget: { value: string } })
                          .currentTarget.value
                      )
                    }
                  />

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                      paddingTop: "24px",
                    }}
                  >
                    <s-checkbox
                      {...({
                        label: "¿Está incluido en el precio?",
                        checked: settings.included_in_price,
                        onChange: (event: any) =>
                          updateSetting(
                            "included_in_price",
                            event.currentTarget.checked
                          ),
                      } as any)}
                    />
                  </div>
                </s-grid>
              </s-stack>
            </s-box>
          </s-grid>
        </s-section>

        <s-box padding="base" />
      </s-page>
    </div>
  );
};

export default TaxEditorPage;
